/// <amd-module name="Dashboards/WidgetHost/Helpers/WidgetHostTelemetry" />
import { DashboardsTelemetryConstants } from "Dashboards/Common/TelemetryConstants";
import { WidgetPresentationMode } from "Dashboards/WidgetContracts/WidgetProps";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { traceWarning } from "VSS/Platform/Trace";

export interface WidgetTelemetryInformation {
    /** Unique Id of the widget (where it exists). */
    widgetId: string | undefined;

    /** Fully qualified Contribution Id for the widget. */
    contributionId: string;

    /** Used to distinguish the different implementations "LegacyWidgetHost" + "ModernWidgetHost" and "ClassicWidgetHost" (For Legacy widgets on Legacy Dashboard). */
    widgetHostType: string;

    /** Used to describe the mode of rendering. New Platform only. */
    presentationMode: WidgetPresentationMode | undefined;

    /** Used to describe which page is rendering the Widget. New Platform only.*/
    parentPageName?: string;
}

/**
 * Tracks widget load behavior for traffic assessments.
 */
export function publishWidgetHostLoadStarted(pageContext: IVssPageContext, widgetTelemetryInformation: WidgetTelemetryInformation): void {
    const telemetryService = pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
    const featureName = "onWidgetHostLoadStarted"; // Note: This is Mirrored in Legacy Dashboard Telemetry.
    telemetryService.publishEvent(DashboardsTelemetryConstants.Area, featureName, widgetTelemetryInformation);
}

export interface WidgetErrorInformation {
    /** A string categorize to aid in analysis of errors.  */
    errorType: string;

    /** An error string or object for user facing presentation, in whatever form we get. */
    error: any;

    /**
     * Advanced, diagnostic telemetry-only information for failures. Not presented to users.
     */
    extendedDiagnosticDetail?: any;
}

export function publishWidgetHostLoadFailed(
    pageContext: IVssPageContext,
    widgetTelemetryInformation: WidgetTelemetryInformation,
    errorInfo: WidgetErrorInformation
): void {
    const interpretedResult = getErrorPropertiesForTelemetry(errorInfo.error);
    const properties = {
        errorType: errorInfo.errorType,
        errorExtendedDetail: getErrorExtendedDetail(errorInfo.extendedDiagnosticDetail),
        ...interpretedResult,
        ...widgetTelemetryInformation
    };

    const telemetryService = pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
    const featureName = "WidgetLoadFailed"; // Note: This is Mirrored in Legacy Dashboard Telemetry.
    telemetryService.publishEvent(DashboardsTelemetryConstants.Area, featureName, properties);
}

/*  Ported from legacy widget telemetry. Extract out unique VS#/TF# error ID string if it's present, to aid in bucketization. Returns null, if no such error is on hand.  */
function extractErrorId(errorMessage: string): string | null {
    let errorId = null;
    let errorIdMatches = errorMessage.match("VS[0-9]+");
    if (errorIdMatches != null && errorIdMatches.length >= 1) {
        errorId = errorIdMatches[0];
    } else {
        errorIdMatches = errorMessage.match("TF[0-9]+");
        if (errorIdMatches != null && errorIdMatches.length >= 1) {
            errorId = errorIdMatches[0];
        }
    }
    return errorId;
}

export function getErrorPropertiesForTelemetry(error: any): { [key: string]: any } {
    const errorMessage = getErrorMessageForTelemetry(error);
    const errorId = extractErrorId(errorMessage);
    const properties: { [key: string]: any } = { errorMessage, errorId };
    if (error.message && error.stack) {
        properties["errorStack"] = error.stack;
    }
    return properties;
}

// Ported from legacy getErrorMessage to handle heterogeneous error messages from widgets
function getErrorMessageForTelemetry(error: any): string {
    if (error) {
        if (typeof error === "string") {
            return error;
        } else if (error.message) {
            return error.message;
        } else if (error.description) {
            return error.description;
        } else if (typeof error === "function") {
            return getErrorMessageForTelemetry(error());
        } else {
            return error.toString();
        }
    } else {
        return "Error message could not be extracted.";
    }
}

function getErrorExtendedDetail(errorExtendedDetail: any) {
    if (errorExtendedDetail instanceof Error) {
        /**
         * The built-in properties of an Error object are not enumerable and thus
         * won't show up in the object JSON serialization. To get around that, those
         * properties need to be redefined as enumerable, under a different name.
         * With that, the value of properties such as message are output in the serialization.
         */
        return {
            ...errorExtendedDetail,
            ...getErrorPropertiesForTelemetry(errorExtendedDetail)
        };
    }

    return errorExtendedDetail;
}

export function publishWidgetHostInteraction(pageContext: IVssPageContext, featureName: string, contributionId: string): void {
    const telemetryService = pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
    telemetryService.publishEvent(DashboardsTelemetryConstants.Area, featureName, {
        contributionId: contributionId
    });
}

export function traceWidgetHostWarning(pageContext: IVssPageContext, featureName: string, description: string, contributionId: string) {
    traceWarning(pageContext, DashboardsTelemetryConstants.Area, featureName, description, {
        contributionId: contributionId
    });
}
